import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatGBP, formatDateUK } from "@/lib/api";

export const conflictFrom = (err) => {
  const d = err.response?.data?.detail;
  return err.response?.status === 409 && ["duplicate_invoice", "cross_store_warning"].includes(d?.code) ? d : null;
};

function ExistingRecord({ existing }) {
  return (
    <div className="rounded-lg border border-navy/10 bg-white p-3 text-sm space-y-1" data-testid="duplicate-existing-record">
      <p className="text-navy font-medium">Invoice {existing.invoice_number}</p>
      <p className="text-navy/60">{formatDateUK(existing.invoice_date)} · {formatGBP(existing.total_amount)} · {existing.status}</p>
      <p className="text-navy/60">Recorded by {existing.submitted_by_name}</p>
      <Link to={`/view-purchase/${existing.id}`} className="inline-flex min-h-[44px] items-center text-gold font-medium" data-testid="duplicate-open-existing-link">
        Open existing invoice
      </Link>
    </div>
  );
}

export default function DuplicateDialog({ conflict, busy, onClose, onRequestException, onContinue }) {
  const [reason, setReason] = useState("");
  if (!conflict) return null;
  const sameStore = conflict.code === "duplicate_invoice";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[94vw] sm:max-w-md" data-testid="duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="text-navy flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> {sameStore ? "Duplicate invoice blocked" : "Possible duplicate at another store"}
          </DialogTitle>
          <DialogDescription className="text-sm text-navy/70" data-testid="duplicate-message">{conflict.message}</DialogDescription>
        </DialogHeader>
        {sameStore && conflict.existing && <ExistingRecord existing={conflict.existing} />}
        {sameStore ? (
          <div className="space-y-2">
            <p className="text-xs text-navy/60">If this really is a separate invoice, explain why. It will be saved as a draft and a manager must approve the exception before it can be submitted.</p>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (at least 10 characters)" className="gm-input" data-testid="duplicate-reason-input" />
            <Button className="w-full min-h-[48px] bg-gold text-navy hover:bg-gold-light" disabled={busy || reason.trim().length < 10}
              onClick={() => onRequestException(reason.trim())} data-testid="duplicate-request-exception-btn">
              Save draft & ask a manager
            </Button>
          </div>
        ) : (
          <Button className="w-full min-h-[48px] bg-gold text-navy hover:bg-gold-light" disabled={busy} onClick={onContinue} data-testid="cross-store-continue-btn">
            This invoice is for this store — continue
          </Button>
        )}
        <Button variant="outline" className="w-full min-h-[48px]" onClick={onClose} data-testid="duplicate-cancel-btn">Go back and check</Button>
      </DialogContent>
    </Dialog>
  );
}
